import apiMonolito from './apiMonolito';
import apiAuth from './apiAuth';
import LZString from 'lz-string';
import { traducir, convertUTCDateToLocalDate } from '@/utils';
import dateFormat from 'dateformat';
import store from '@/store';

// Importar nuevo API para uso interno durante migración
import * as appointmentsApi from './appointmentsApi';

/**
 * Servicios para el monolito usando apiMonolito con interceptores automáticos
 * 
 * NOTA: Las funciones de turnos/appointments están siendo migradas a appointmentsApi.js
 * Las funciones marcadas como @deprecated serán eliminadas en futuras versiones.
 * 
 * Para nuevos desarrollos, importar directamente:
 * import { getTurnosDisponibles, reservarTurno } from '@/services/appointmentsApi';
 */

// Función helper para manejar descompresión y parsing seguro
function safeDecompressAndParse(data, fallbackValue = []) {
  if (!data) {
    return fallbackValue;
  }

  // Allow plain objects/arrays (mocking or uncompressed API)
  if (typeof data === 'object' && data !== null) {
    return data;
  }

  if (typeof data === 'string') {
    // 1. Intentar parsear directamente como JSON no comprimido
    try {
      const parsedJson = JSON.parse(data);
      if (parsedJson && (typeof parsedJson === 'object' || Array.isArray(parsedJson))) {
        return parsedJson;
      }
    } catch (e) {
      // No es un string JSON directo, continuar con descompresión
    }

    // 2. Intentar descomprimir como LZString en base64
    try {
      const decodedContent = LZString.decompressFromBase64(data);
      if (decodedContent) {
        const parsed = JSON.parse(decodedContent);
        if (parsed) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Error descompresión LZString:', e);
    }
  }

  return fallbackValue;
}

// Función helper para manejar errores de API de manera elegante
function handleApiError(error, defaultValue = []) {
  if (error.code === 'ERR_NETWORK') {
    return defaultValue;
  }
  if (error.response && error.response.status >= 500) {
    return defaultValue;
  }
  return defaultValue;
}

function normalizePatientPayload(pacienteData = {}) {
  const payload = { ...pacienteData };

  if (payload.mail && !payload.email) {
    payload.email = payload.mail;
  }
  if (payload.email && !payload.mail) {
    payload.mail = payload.email;
  }

  if (payload.telefono && !payload.phone) {
    payload.phone = payload.telefono;
  }
  if (payload.phone && !payload.telefono) {
    payload.telefono = payload.phone;
  }

  if (payload.insuranse && !payload.insurance_id) {
    payload.insurance_id = payload.insuranse;
  }
  if (payload.insurance_id && !payload.insuranse) {
    payload.insuranse = payload.insurance_id;
  }

  return payload;
}

// Servicios de profesionales
export async function getProfesionales() {
  try {
    const response = await apiMonolito.get('/profesional');

    // Los datos están comprimidos con LZ-String, necesitamos descomprimirlos
    const decompressedData = safeDecompressAndParse(response.data, []);

    return decompressedData;
  } catch (error) {
    console.error('Error en getProfesionales:', error);
    return handleApiError(error, []);
  }
}

export async function getProfesionalByDni(dni) {
  try {
    const response = await apiMonolito.get(`/profesional/${dni}`);
    return response.data;
  } catch (error) {
    return handleApiError(error, null);
  }
}

export async function getProfesionalesABM(lastname = '', includeInactive = true) {
  try {
    let url = '/profesional?raw=true';
    if (lastname && lastname.trim() !== '') {
      url += `&lastname=${encodeURIComponent(lastname.trim())}`;
    }
    if (includeInactive) {
      url += `&include_inactive=true`;
    }
    const response = await apiMonolito.get(url);
    const data = safeDecompressAndParse(response.data, []);
    return data;
  } catch (error) {
    console.error('Error en getProfesionalesABM:', error);
    return [];
  }
}

export async function createProfesionalABM(profesionalData) {
  try {
    const response = await apiMonolito.post('/profesional', profesionalData);
    return response.data;
  } catch (error) {
    console.error('Error en createProfesionalABM:', error);
    throw error;
  }
}

export async function updateProfesionalABM(profesionalId, profesionalData) {
  try {
    const response = await apiMonolito.put(`/profesional/${profesionalId}`, profesionalData);
    return response.data;
  } catch (error) {
    console.error('Error en updateProfesionalABM:', error);
    throw error;
  }
}

export async function darDeBajaProfesional(profesionalId) {
  try {
    const response = await apiMonolito.delete(`/profesional/${profesionalId}`);
    return response.data;
  } catch (error) {
    console.error('Error en darDeBajaProfesional:', error);
    throw error;
  }
}

export function decodeProfesional(data) {

  // Verificar que data sea un array válido y no esté vacío
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  let newJson = [];
  let especialidad;

  for (let item in data) {
    try {
      // Verificar que el item tenga la estructura esperada
      if (!data[item] || !data[item].party) {
        continue;
      }

      if (data[item].specialty == null) {
        especialidad = "";
      } else {
        especialidad = data[item].specialty.name;
      }

      let profesionalId = data[item].id || data[item].party.id || item;

      newJson.push({
        text: data[item].party.name + " " + data[item].party.lastname + " - " + traducir(especialidad),
        value: profesionalId,
        id: profesionalId
      });
    } catch (error) {
      console.error('Error procesando profesional', item, error);
    }
  }


  return newJson;
}

// ============================================================================
// SERVICIOS DE CITAS/TURNOS (LEGACY)
// NOTA: Usar appointmentsApi.js para nuevos desarrollos
// ============================================================================

/**
 * @deprecated Usar getTurnosDisponibles de appointmentsApi.js
 * Obtiene turnos de un profesional (endpoint legacy)
 */
export async function getAppointments(profesionalId = 0) {
  try {
    const response = await apiMonolito.get(`/events/${profesionalId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * @deprecated Usar getTurnosDoctor de appointmentsApi.js
 */
export async function getAppointmentsDr(profesional, historial = false) {
  let url = historial ? `/appointmentsdr-historial/${profesional}` : `/appointmentsdr/${profesional}`;

  try {
    const response = await apiMonolito.get(url);
    return response.data;
  } catch (error) {
    return 404;
  }
}

/**
 * @deprecated Usar getTurnosDisponibles de appointmentsApi.js
 */
export async function getAppointmentsAuth(userId, idProfesional) {
  try {
    const encodedUserId = encodeURIComponent(userId);
    const encodedProfesionalId = encodeURIComponent(idProfesional);

    const response = await apiMonolito.get(`/events/${encodedUserId}/${encodedProfesionalId}`);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      return [];
    }
    return [];
  }
}

export function decodeEventos(initial_data) {

  // Verificar que initial_data sea un array válido
  if (!Array.isArray(initial_data)) {
    return {
      citas: [],
      pacientes: []
    };
  }

  let pacientes = [];
  let jsonLocale = [];

  for (let x of initial_data) {
    if (x.appointment_date) {
      let fechaUTC = convertUTCDateToLocalDate(x.appointment_date);
      let fechaEnd = convertUTCDateToLocalDate(x.appointment_date_end);
      fechaUTC = dateFormat(fechaUTC, "yyyy-mm-dd HH:MM:ss");
      fechaEnd = dateFormat(fechaEnd, "yyyy-mm-dd HH:MM:ss");
      x.appointment_date = fechaUTC;
      x.appointment_date_end = fechaEnd;
      jsonLocale.push(x);
    } else {
      pacientes.push(x);
    }
  }

  let datos = jsonLocale;
  pacientes = JSON.parse(JSON.stringify(pacientes).split('"name":').join('"nombre":'));
  let data = [];

  for (let i = 0; i < datos.length; i++) {
    data.push(Object.assign(datos[i], pacientes[i]));
  }

  let json = JSON.parse(JSON.stringify(data).split('"appointment_date":').join('"start":'));
  json = JSON.parse(JSON.stringify(json).split('"appointment_date_end":').join('"end":'));
  json = JSON.parse(JSON.stringify(json).split('"name":').join('"title":'));
  json = JSON.parse(JSON.stringify(json).split('"id":').join('"id":'));

  return { "citas": json, "pacientes": pacientes };
}

export async function createAppointment(appointmentData) {
  try {
    const response = await apiMonolito.post('/events', appointmentData);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateAppointment(appointmentId, appointmentData) {
  try {
    const response = await apiMonolito.put(`/events/${appointmentId}`, appointmentData);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteAppointment(appointmentId) {
  try {
    const response = await apiMonolito.delete(`/events/${appointmentId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * @deprecated Usar reservarTurno de appointmentsApi.js
 */
export async function createAppointmentWithUrl(url, dni, event) {
  try {
    // Si event tiene datos del paciente (name, lastname, telefono), incluirlos en el payload
    const payload = { "dni": dni };

    // Check if event object has patient data (passed from CitasPublico)
    if (event.name) payload.name = event.name;
    if (event.lastname) payload.lastname = event.lastname;
    if (event.telefono) payload.phone = event.telefono;
    if (event.mail) payload.email = event.mail;

    const response = await apiMonolito.patch(`${url}${dni}/${event.id}`, payload);
    return 200;
  } catch (error) {
    if (error.response && error.response.status === 423) {
      return 423;
    } else {
      return 400;
    }
  }
}

/**
 * @deprecated Usar crearSobreturno de appointmentsApi.js
 */
export async function crearSobreTurno(dni, profesional_id, citaData) {
  let url = '/new_appointments/';
  let date = citaData['fecha_visita'];
  let tipo_visita = citaData['tipo_visita'];

  if (tipo_visita == '') {
    tipo_visita = "Consulta de seguimiento";
  }
  if (!date) {
    date = new Date();
  }

  try {
    const response = await apiMonolito.put(`${url}${dni}/${profesional_id}`, {
      "date": date,
      "tipo_visita": tipo_visita
    });
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * @deprecated Usar cambiarEstadoTurno de appointmentsApi.js
 */
export async function cambiarEstado(event, estado) {
  try {
    const response = await apiMonolito.patch(`/appointmentsstate/${event.id}`, {
      eventID: event.id,
      estado: estado
    });
    return response;
  } catch (error) {
    return 404;
  }
}

/**
 * @deprecated Usar reservarTurnoSecretaria de appointmentsApi.js
 */
export async function confirmarTurno(event, account_info) {
  try {
    const response = await apiMonolito.patch(`/appointmentsscr/${event}`, account_info);
    return { status: 200, data: response.data };
  } catch (error) {
    return { status: 404, error: error.message };
  }
}

// Servicios de agenda de trabajo
export async function deleteWorkCalendar(profesionalId, workCalendarData) {
  try {
    // Se envía workCalendarData (fechas/días) en el body
    const response = await apiMonolito.put(`/deleteworkcalendar/${profesionalId}`, workCalendarData);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Servicios de datos
export async function getData() {
  try {
    const response = await apiMonolito.get('/data');
    const datos = safeDecompressAndParse(response.data, []);
    return datos;
  } catch (error) {
    return handleApiError(error, []);
  }
}

export async function getDataDr() {
  try {
    const response = await apiMonolito.get('/especialidades/');
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Obtiene la lista de obras sociales disponibles
 * @returns {Promise<Array>} Lista de obras sociales [{value: id, name: nombre}]
 */
export async function getObrasSociales() {
  try {
    // Los datos están en /data, que devuelve [obras_sociales, provincias]
    const datos = await getData();

    return obrasSociales;
  } catch (error) {
    return [];
  }
}

/**
 * Verifica si existe un paciente por email en el monolito
 * @param {string} email - Email del usuario
 * @returns {Promise<Object|null>} Datos del paciente o null si no existe
 */
export async function getPersonByEmail(email) {
  if (!email) {
    return null;
  }

  try {
    const response = await apiMonolito.get('/person/' + email);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Crea un nuevo paciente en el monolito
 * @param {string} email - Email del usuario (account_id)
 * @param {Object} personData - Datos del paciente
 * @returns {Promise<Object>} Respuesta del servidor
 */
export async function createPersonInMonolito(email, personData) {
  if (!email) {
    throw new Error('El email es requerido para crear el paciente');
  }

  if (!personData || !personData.dni || !personData.name || !personData.lastname) {
    throw new Error('Datos incompletos: dni, name y lastname son requeridos');
  }

  try {
    const normalizedData = normalizePatientPayload(personData);

    // Preparar datos para el endpoint POST /people/{email}
    const payload = {
      dni: normalizedData.dni,
      name: normalizedData.name,
      lastname: normalizedData.lastname,
      dob: normalizedData.dob,
      gender: normalizedData.gender,
      phone: normalizedData.phone || null,
      insurance_id: normalizedData.insurance_id || null
    };

    const response = await apiMonolito.post('/people/' + email, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Servicios de personas
export async function getPeople(dni = null, params = {}) {
  try {
    let url;
    let pacienteId = store.getters.userPayload.email;

    // Si se proporciona pacienteId, usar /people/{id} para obtener paciente específico
    if (dni && typeof dni === 'string' && dni.trim() !== '') {
      url = `/persona/${dni}`;
    } else if (!dni) {
      url = `/person/${pacienteId}`;
    } else {
      // Si no hay pacienteId, usar /paciente_list para paginación
      url = '/paciente_list';
    }

    // Agregar parámetros de query si se proporcionan
    if (Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key]);
        }
      });
      url += `?${queryParams.toString()}`;
    }

    const response = await apiMonolito.get(url);
    return response.data;
  } catch (error) {
    // Manejar errores específicos
    if (error.code === 'ERR_NETWORK') {
      throw new Error('No se puede conectar al servidor monolito. Verificar que esté corriendo en http://localhost:5000');
    }

    if (error.response && error.response.status === 0) {
      throw new Error('Error de CORS. El servidor monolito debe configurar CORS para permitir requests desde http://localhost:8446');
    }

    if (error.response && error.response.status === 405) {
      const endpoint = dni ? `/persona/${dni}` : '/paciente_list';
      throw new Error(`El endpoint ${endpoint} no soporta el método GET o no está configurado correctamente`);
    }

    throw error;
  }
}

export async function createPerson(accountId, personData) {
  try {
    const response = await apiMonolito.post(`/people/${accountId}`, personData);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updatePerson(accountId, personData) {
  if (!accountId) {
    return 400;
  }

  try {
    await apiMonolito.patch(`/person/${accountId}`, personData);
    return 200;
  } catch (error) {
    if (error.code === 'ERR_NETWORK') {
      return 500;
    }
    return 404;
  }
}

export async function getPacienteData(patient, identifier) {
  if (!identifier) {
    return 400;
  }

  let url = !patient ? `/persona/${identifier}` : `/person/${identifier}`;

  try {
    const response = await apiMonolito.get(url);
    return response.data;
  } catch (error) {
    if (error.code === 'ERR_NETWORK') {
      return 500;
    }
    return 404;
  }
}

export async function getPaciente(patient_id) {
  try {
    const response = await apiMonolito.get(`/paciente-detail/${patient_id}`);
    return response.data;
  } catch (error) {
    return 404;
  }
}

export async function updatePaciente(account_info) {
  const normalizedPayload = normalizePatientPayload(account_info);
  let account_id = normalizedPayload.dni;

  if (!account_id) {
    return 400;
  }



  try {
    const response = await apiMonolito.patch(`/paciente-detail/${account_id}`, normalizedPayload);
    return 200;
  } catch (error) {
    if (error.code === 'ERR_NETWORK') {
      return 500;
    }
    return 404;
  }
}

/**
 * @deprecated Usar getTurnosPaciente de appointmentsApi.js
 */
export async function getAppointmentsPaciente(paciente = null) {
  if (!paciente) {
    return [];
  }

  let url = `/turnospaciente/${paciente}`;

  try {
    const response = await apiMonolito.get(url);
    return response.data;
  } catch (error) {
    return [];
  }
}

// ============================================================================
// SERVICIOS DE HISTORIA CLÍNICA
// ============================================================================

export async function getHistoriaClinica(paciente) {
  try {
    const response = await apiMonolito.get(`/historia-clinica/${paciente}`);
    const datos = safeDecompressAndParse(response.data, []);
    return datos;
  } catch (error) {
    return handleApiError(error, []);
  }
}

export async function createHistoriaClinica(paciente, historia) {
  try {
    const response = await apiMonolito.post(`/historia-clinica/${paciente}`, historia);
    return 200;
  } catch (error) {
    if (error.response && error.response.status === 423) {
      return 423;
    } else {
      return 400;
    }
  }
}

export async function updateHistoriaClinica(paciente, consultaId, historia) {
  try {
    const response = await apiMonolito.put(`/historia-clinica/${paciente}/${consultaId}`, historia);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 403) {
      return 403;
    }

    return 400;
  }
}

// Servicios de indicadores
export async function getIndicadores() {
  try {
    const response = await apiMonolito.get('/indicadores');
    return response.data;
  } catch (error) {
    console.error('Error obteniendo indicadores:', error);
    return 404;
  }
}

// Servicios de medicamentos
export async function getMedicamentos() {
  try {
    const response = await apiMonolito.get('/medicamentos');
    return response.data;
  } catch (error) {
    return 404;
  }
}

// Servicios de código SMS
export async function sendCode(dni, telefono) {
  try {
    const response = await apiMonolito.patch('/sendcode', {
      "telefono": telefono,
      "dni": dni
    });
    return true;
  } catch (error) {
    return false;
  }
}

export async function validateCode(dni, code) {
  try {
    const response = await apiMonolito.put('/sendcode', {
      "codigo": code,
      "dni": dni
    });
    return true;
  } catch (error) {
    return false;
  }
}

// Servicios de forgot password
export async function forgotPassword(email) {
  try {
    const response = await apiMonolito.get(`/forgot?username=${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Servicios de resend email
export async function resendEmail(email) {
  try {
    const response = await apiMonolito.get(`/newresend?username=${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Servicios de work calendar
export async function createWorkCalendar(profesionalId, workCalendarData) {
  try {
    const response = await apiMonolito.put(`/workcalendar/${profesionalId}`, workCalendarData);
    return response.data;

  } catch (error) {
    // Si es error 401, es el problema conocido del backend
    if (error.response?.status === 401) {

      // Crear error específico para el usuario
      const backendError = new Error('El sistema tiene un problema de configuración del servidor que impide crear calendarios de trabajo. El backend del monolito no está reconociendo correctamente los tokens de autenticación para esta operación específica.');
      backendError.isBackendIssue = true;
      backendError.originalError = error;
      backendError.recommendation = 'Contacte al administrador del sistema para revisar la configuración de autenticación del monolito (puerto 8450) específicamente para endpoints PUT /workcalendar/.';
      throw backendError;
    }

    // Para otros errores, manejo normal
    throw error;
  }
}// Servicios de profesional horario
export async function createProfesionalHorario(profesionalData) {
  try {
    const response = await apiMonolito.put('/profesionalhorario/', profesionalData);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Servicios de appointments state
export async function updateAppointmentState(eventId, estado) {
  try {
    const response = await apiMonolito.patch(`/appointmentsstate/${eventId}`, {
      eventID: eventId,
      estado: estado
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}



// Servicios de appointments confirm
export async function confirmAppointment(eventId) {
  try {
    const response = await apiMonolito.patch(`/appointments/${eventId}`, {
      eventID: eventId
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Servicio unificado para consultar pacientes
export async function getPacienteUnificado(criterio = null, params = {}) {
  try {
    let url;
    let tipoConsulta = 'paginacion';

    // Determinar el tipo de consulta basado en el criterio
    if (criterio) {
      // Verificar si es un email (contiene @)
      if (typeof criterio === 'string' && criterio.includes('@')) {
        url = `/person/${criterio}`;
        tipoConsulta = 'email';
      }
      // Verificar si es un DNI (solo números)
      else if (typeof criterio === 'string' && /^\d+$/.test(criterio)) {
        url = `/persona/${criterio}`;
        tipoConsulta = 'dni';
      }
      // Si no es email ni DNI, asumir que es un ID de paciente
      else {
        url = `/paciente-detail/${criterio}`;
        tipoConsulta = 'id';
      }
    } else {
      // Si no hay criterio, usar paginación
      url = '/paciente_list';
    }

    // Agregar parámetros de query si se proporcionan
    if (Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key]);
        }
      });
      url += `?${queryParams.toString()}`;
    }

    const response = await apiMonolito.get(url);

    // Devolver objeto con datos y metadatos
    return {
      data: response.data,
      tipoConsulta: tipoConsulta,
      criterio: criterio,
      success: true
    };

  } catch (error) {

    // Manejar errores específicos
    if (error.code === 'ERR_NETWORK') {
      throw new Error('No se puede conectar al servidor monolito. Verificar que esté corriendo en http://localhost:5000');
    }

    if (error.response && error.response.status === 0) {
      throw new Error('Error de CORS. El servidor monolito debe configurar CORS para permitir requests desde http://localhost:8446');
    }

    if (error.response && error.response.status === 404) {
      return {
        data: null,
        tipoConsulta: criterio ? 'no_encontrado' : 'paginacion',
        criterio: criterio,
        success: false,
        error: 'Paciente no encontrado'
      };
    }

    return {
      data: null,
      tipoConsulta: 'error',
      criterio: criterio,
      success: false,
      error: error.message
    };
  }
}

// Servicio unificado para crear/actualizar pacientes
export async function savePacienteUnificado(pacienteData, operacion = 'update') {
  try {
    const normalizedPayload = normalizePatientPayload(pacienteData);
    let url;
    let method;

    // Determinar el endpoint y método basado en la operación
    if (operacion === 'create') {
      // Para crear, necesitamos un accountId
      const accountId = normalizedPayload.email || normalizedPayload.dni;
      if (!accountId) {
        throw new Error('Para crear un paciente se requiere email o DNI');
      }
      url = `/people/${accountId}`;
      method = 'post';
    } else {
      // Para actualizar, usar DNI como identificador principal
      const accountId = normalizedPayload.dni;
      if (!accountId) {
        throw new Error('Para actualizar un paciente se requiere DNI');
      }
      url = `/paciente-detail/${accountId}`;
      method = 'patch';
    }

    const response = await apiMonolito[method](url, normalizedPayload);

    return {
      success: true,
      data: response.data,
      operacion: operacion
    };

  } catch (error) {
    console.error('Error en savePacienteUnificado:', error);

    if (error.code === 'ERR_NETWORK') {
      console.error('Error de conexión al monolito. Verificar que el servidor esté corriendo en http://localhost:5000');
      return {
        success: false,
        error: 'Error de conexión al servidor',
        operacion: operacion
      };
    }

    if (error.response && error.response.status === 423) {
      return {
        success: false,
        error: 'El paciente ya existe',
        operacion: operacion
      };
    }

    return {
      success: false,
      error: error.message,
      operacion: operacion
    };
  }
} 